import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import Hero from "@/components/sections/Hero";
import Goal from "@/components/sections/Goal";
import ActionTakers from "@/components/sections/ActionTakers";
import ReadyToWin from "@/components/sections/ReadyToWin";
import Pricing from "@/components/sections/Pricing";
import Bonus from "@/components/sections/Bonus";
import Portfolio from "@/components/sections/Portfolio";
import Founder from "@/components/sections/Founder";
import FinalCta from "@/components/sections/FinalCta";
import Footer from "@/components/sections/Footer";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("all_star");
  const [paymentStatus, setPaymentStatus] = useState(null); // {status, message}
  const [spots, setSpots] = useState({ total: 10, claimed: 0, remaining: 10 });

  const fetchSpots = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/spots`);
      if (res.data) setSpots(res.data);
    } catch (e) {
      // ignore — defaults stand in
    }
  }, []);

  useEffect(() => {
    fetchSpots();
    const t = setInterval(fetchSpots, 30000);
    return () => clearInterval(t);
  }, [fetchSpots]);

  const openModal = useCallback((pkg = "all_star") => {
    setSelectedPackage(pkg);
    setModalOpen(true);
  }, []);

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;
    if (attempts >= maxAttempts) {
      setPaymentStatus({
        type: "warning",
        message: "Payment is taking longer than usual. Check your email for confirmation.",
      });
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;
      if (data.payment_status === "paid") {
        setPaymentStatus({
          type: "success",
          message: `🏀 Payment successful! Your ${data.package_name} spot is locked in. Chisesi will be in touch shortly.`,
        });
        toast.success("Payment confirmed!");
        return;
      }
      if (data.status === "expired") {
        setPaymentStatus({
          type: "error",
          message: "Payment session expired. Please try again.",
        });
        return;
      }
      setPaymentStatus({
        type: "pending",
        message: "Processing your payment…",
      });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (e) {
      setPaymentStatus({
        type: "error",
        message: "Could not verify payment. Please refresh or contact support.",
      });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");
    if (sessionId) {
      setPaymentStatus({ type: "pending", message: "Confirming your payment…" });
      pollPaymentStatus(sessionId);
    } else if (canceled) {
      setPaymentStatus({
        type: "warning",
        message: "Checkout was canceled. Your spot isn't claimed yet — try again when you're ready.",
      });
    }
  }, [pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] antialiased">
      <Toaster position="top-center" richColors />
      {paymentStatus && (
        <div
          data-testid={LANDING.paymentStatus}
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-xl w-[92%] rounded-2xl px-5 py-3 text-sm md:text-base font-semibold shadow-2xl backdrop-blur-xl border ${
            paymentStatus.type === "success"
              ? "bg-white text-[#0A0A0A] border-[#00B2A9]"
              : paymentStatus.type === "error"
              ? "bg-white text-[#0A0A0A] border-[#EF426F]"
              : "bg-white text-[#0A0A0A] border-black/10"
          }`}
        >
          {paymentStatus.message}
        </div>
      )}

      <Hero onClaim={() => openModal("all_star")} spots={spots} />
      <Goal onClaim={() => openModal("all_star")} />
      <ActionTakers onClaim={() => openModal("all_star")} />
      <ReadyToWin />
      <Pricing onClaim={(pkg) => openModal(pkg)} />
      <Bonus />
      <Portfolio />
      <Founder />
      <FinalCta onClaim={() => openModal("all_star")} spots={spots} />
      <Footer />

      <LeadCaptureModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultPackage={selectedPackage}
        apiBase={API}
      />
    </div>
  );
};

export default LandingPage;
